import {useCallback, useReducer} from 'react';

export interface ProfileComparisonSelectionState {
  profileAId: string | null;
  profileBId: string | null;
}

interface SelectionState {
  profileAId: string | null;
  profileBId: string | null;
}

type SelectionAction =
  | {type: 'toggle'; profileId: string}
  | {type: 'deselect'; slot: 'a' | 'b'}
  | {type: 'clear'};

const initialState: SelectionState = {
  profileAId: null,
  profileBId: null,
};

const selectionReducer = (state: SelectionState, action: SelectionAction): SelectionState => {
  switch (action.type) {
    case 'clear':
      return initialState;
    case 'deselect':
      return action.slot === 'a' ? {...state, profileAId: null} : {...state, profileBId: null};
    case 'toggle': {
      const {profileId} = action;

      if (state.profileAId === profileId) {
        return {...state, profileAId: null};
      }

      if (state.profileBId === profileId) {
        return {...state, profileBId: null};
      }

      if (!state.profileAId) {
        return {...state, profileAId: profileId};
      }

      if (!state.profileBId) {
        return {...state, profileBId: profileId};
      }

      return {...state, profileBId: profileId};
    }
    default:
      return state;
  }
};

export const useProfileComparisonSelection = () => {
  const [state, dispatch] = useReducer(selectionReducer, initialState);

  const toggleProfile = useCallback((profileId: string) => {
    dispatch({type: 'toggle', profileId});
  }, []);

  const deselectSlot = useCallback((slot: 'a' | 'b') => {
    dispatch({type: 'deselect', slot});
  }, []);

  const clearSelection = useCallback(() => {
    dispatch({type: 'clear'});
  }, []);

  const isComparisonReady = state.profileAId !== null && state.profileBId !== null;

  return {
    profileAId: state.profileAId,
    profileBId: state.profileBId,
    isComparisonReady,
    toggleProfile,
    deselectSlot,
    clearSelection,
  };
};
